import type { SnEvent } from './types'

export interface EventRecurrence {
  /** 'weekly' | 'biweekly' | 'monthly', or '' for a one-off. */
  recurrenceType: string
  /** 0=Sunday … 6=Saturday. */
  daysOfWeek: number[]
  /** UTC ISO cut-off, or '' when absent. */
  repeatUntil: string
  /** Length of each occurrence in minutes, or null when the API omits it. */
  durationMinutes: number | null
}

type RecurrenceSource = Pick<
  SnEvent,
  'recurrence_type' | 'series' | 'days_of_week' | 'repeat_until'
>

/**
 * The repeat rule for an event, from wherever the API put it.
 *
 * The detail response nests the rule in a `series` object shared by every
 * occurrence; older/other responses send `days_of_week` and `repeat_until`
 * flat at the top level. Series wins when present — it is the authoritative
 * rule — with the flat fields as a fallback so both shapes work.
 *
 * Returns null for a one-off, so callers can hide the whole block.
 */
export const getEventRecurrence = (
  event: RecurrenceSource | null | undefined
): EventRecurrence | null => {
  const series = event?.series
  // The occurrence and the series should agree, but the series is the rule.
  const recurrenceType = series?.recurrence_type ?? event?.recurrence_type

  // 'none' is a UI-only sentinel that should never come back from the API; it
  // is treated as one-off here rather than rendering a "None" row.
  if (!recurrenceType || recurrenceType === 'none') {
    return null
  }

  const days = series?.days_of_week ?? event?.days_of_week
  const repeatUntil = series?.repeat_until ?? event?.repeat_until
  const duration = series?.duration_minutes

  return {
    recurrenceType,
    daysOfWeek: Array.isArray(days) ? days : [],
    repeatUntil: repeatUntil ?? '',
    durationMinutes: typeof duration === 'number' ? duration : null,
  }
}
